import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';

import { cartao, cor, espaco, texto } from '#components/finapp/estilo';
import { useLocale } from '#hooks/useLocale';

import { interpretarValor } from './valorDigitado';
import { mesesSemODia, primeiroVencimento } from './vencimento';

type Props = {
  /** Chamado depois de criar, para a lista recarregar. */
  onCriado: () => void;
  /**
   * O aviso de "vencimento em outro mês" — CONTROLADO pelo pai (`Compromissos`).
   *
   * ⚠️⚠️ Não é `useState` local de propósito: `onCriado` (acima) dispara um
   * remount de toda esta árvore (ver o JSDoc de `Compromissos.tsx`), e um
   * `useState` local seria zerado no mesmo instante em que fosse setado. O
   * aviso precisa morar num componente que NÃO remonta — daí ser prop.
   */
  aviso: string | null;
  onAvisoChange: (aviso: string | null) => void;
};

/**
 * Três campos — nome, valor, dia do mês — que viram um agendado mensal nativo
 * do Actual.
 *
 * ⭐ É a peça que tira o dono da tela de *schedules* do Actual, que é a cara
 * de contabilidade que ele rejeitou. Por baixo é o MESMO objeto: quem quiser
 * o controle fino continua tendo, em Ajustes ▸ Contas fixas.
 *
 * ⚠️ O valor é gravado NEGATIVO (despesa). `interpretarValor` sempre devolve
 * positivo — o sinal é responsabilidade daqui, num lugar só.
 *
 * ⚠️⚠️ FIX ROUND 1 (Important 1) — o `start` da primeira ocorrência NÃO é
 * mais `${mes}-${dia}` direto: passa por `primeiroVencimento` (ver
 * `compromissos/vencimento.ts`), que acha o primeiro mês que REALMENTE tem
 * aquele dia. Sem isso, `monthUtils.parseDate('2026-09-31')` (dia 31 não
 * existe em setembro) rolava silenciosamente pra 1º de outubro — "aluguel
 * todo dia 31" virava, de verdade, "aluguel todo dia 1", sem erro nenhum.
 * Quando o mês do vencimento sai do mês corrente, a tela avisa (`aviso`,
 * abaixo do formulário) em vez de ajustar a data calado.
 *
 * ⚠️⚠️ Revisão final da branch (Finding 3, Important) — o aviso não cobre
 * mais só a PRIMEIRA ocorrência. `patterns: []` faz a recorrência mensal
 * nativa do Actual PULAR, pra sempre, todo mês em que o dia escolhido não
 * existe (RFC5545) — "todo dia 31" nunca ocorre em fev/abr/jun/set/nov, e
 * isso passou a ter consequência nova: `fixasPrevistas` é descontado do
 * "Livre pra decidir" (Task 9/painel-panorama), então num mês em que o
 * aluguel do dia 31 simplesmente não ocorre, esse número fica alto demais —
 * plausível, sem aviso. Por isso QUALQUER `dia > 28` mostra o aviso (via
 * `mesesSemODia`, `compromissos/vencimento.ts`), rolando a primeira
 * ocorrência ou não — não só quando ela sai do mês corrente. ⛔ O motor de
 * recorrência não é alterado (é idêntico ao da tela nativa `/schedules`);
 * isto só torna o comportamento VISÍVEL antes do dono cadastrar.
 */
export function FormularioCompromisso({
  onCriado,
  aviso,
  onAvisoChange,
}: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [nome, setNome] = useState('');
  const [valor, setValor] = useState('');
  const [dia, setDia] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro(null);
    onAvisoChange(null);

    if (nome.trim() === '') {
      setErro(t('Dê um nome ao compromisso.'));
      return;
    }

    const centavos = interpretarValor(valor);
    if (centavos === null) {
      setErro(t('Valor inválido. Exemplo: 1.200,00'));
      return;
    }

    const diaNumero = Number(dia);
    if (!Number.isInteger(diaNumero) || diaNumero < 1 || diaNumero > 31) {
      setErro(t('O dia tem que ser um número de 1 a 31.'));
      return;
    }

    setSalvando(true);
    try {
      /**
       * `start` é a primeira ocorrência: o dia escolhido, no primeiro mês
       * (a partir do corrente) em que ele existe de verdade — ver o JSDoc
       * acima e `compromissos/vencimento.ts`. `patterns: []` +
       * `frequency: 'monthly'` = repete todo mês nesse dia.
       */
      const mes = monthUtils.currentMonth();
      const start = primeiroVencimento(diaNumero, mes);
      const mesDoVencimento = start.slice(0, 7);

      await send('schedule/create', {
        schedule: { name: nome.trim(), posts_transaction: false },
        conditions: [
          {
            op: 'isapprox',
            field: 'date',
            value: {
              start,
              frequency: 'monthly',
              patterns: [],
              skipWeekend: false,
              weekendSolveMode: 'after',
              endMode: 'never',
              endOccurrences: 1,
              endDate: start,
            },
          },
          { op: 'isapprox', field: 'amount', value: -centavos },
        ],
      });

      /**
       * ⚠️⚠️ Revisão final da branch (Finding 3) — dois avisos possíveis,
       * não mais um só. `rolou` cobre a PRIMEIRA ocorrência (o mês em que
       * ela nasce mudou); `mesesQuePulam` cobre TODOS os meses seguintes
       * (o dia simplesmente não existe neles, todo ano). Um compromisso de
       * dia 31 cadastrado num mês de 31 dias não rola (`rolou` é falso) mas
       * AINDA assim precisa do segundo aviso — é o caso que faltava antes
       * desta correção.
       */
      const rolou = mesDoVencimento !== mes;
      const mesesQuePulam = mesesSemODia(diaNumero);

      if (rolou && mesesQuePulam.length > 0) {
        onAvisoChange(
          t(
            'O dia {{dia}} não existe em {{mes}} — o primeiro vencimento ficou em {{data}}. Além disso, como {{dia}} não existe em todo mês, este compromisso não vai aparecer em: {{meses}}.',
            {
              dia: diaNumero,
              mes: monthUtils.format(mes, 'MMMM', locale),
              data: monthUtils.format(start, 'dd/MM', locale),
              meses: mesesQuePulam.join(', '),
            },
          ),
        );
      } else if (rolou) {
        onAvisoChange(
          t(
            'O dia {{dia}} não existe em {{mes}} — o primeiro vencimento ficou em {{data}}.',
            {
              dia: diaNumero,
              mes: monthUtils.format(mes, 'MMMM', locale),
              data: monthUtils.format(start, 'dd/MM', locale),
            },
          ),
        );
      } else if (mesesQuePulam.length > 0) {
        onAvisoChange(
          t(
            'O dia {{dia}} não existe em todo mês — este compromisso não vai aparecer em: {{meses}}.',
            { dia: diaNumero, meses: mesesQuePulam.join(', ') },
          ),
        );
      }

      setNome('');
      setValor('');
      setDia('');
      onCriado();
    } catch (e: unknown) {
      setErro(
        t('Não consegui salvar: {{erro}}', {
          erro: e instanceof Error ? e.message : String(e),
        }),
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <View style={{ ...cartao, gap: 12 }} data-testid="formulario-compromisso">
      <Text style={texto.rotuloCartao}>
        <Trans>Novo compromisso</Trans>
      </Text>

      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <Input
          value={nome}
          onChangeValue={setNome}
          placeholder={t('Aluguel')}
          aria-label={t('Nome do compromisso')}
          style={{ flexGrow: 1, minWidth: 160 }}
        />
        <Input
          value={valor}
          onChangeValue={setValor}
          placeholder={t('1.200,00')}
          aria-label={t('Valor')}
          style={{ width: 140 }}
        />
        <Input
          value={dia}
          onChangeValue={setDia}
          placeholder={t('Dia')}
          aria-label={t('Dia do vencimento')}
          style={{ width: 80 }}
        />
        <Button
          variant="primary"
          onPress={salvar}
          isDisabled={salvando}
          style={{ borderRadius: espaco.raioPequeno }}
        >
          {salvando ? t('Salvando…') : t('Adicionar')}
        </Button>
      </View>

      {erro && (
        <Text style={{ ...texto.corpo, color: cor.saida }} role="alert">
          {erro}
        </Text>
      )}

      {aviso && (
        <Text
          data-testid="formulario-compromisso-aviso"
          style={{ ...texto.corpo, color: cor.tintaFraca }}
        >
          {aviso}
        </Text>
      )}
    </View>
  );
}
